<script setup lang="ts" generic="TCommand extends string">
import { computed } from 'vue'

import { useViewport } from '@/shared/lib'
import { Button } from '@/shared/ui/Button'
import {
  Dropdown,
  DropdownMenu,
  DropdownItem,
  DropdownActionButton
} from '@/shared/ui/Dropdown'
import { Icon } from '@/shared/ui/Icon'

import type { TableAction } from './types'

/**
 * The actions of a table row, as data: a collapsed "Actions ▾" dropdown by default, or an
 * inline row of buttons under `variant="buttons"`. On a narrow screen it is always a
 * dropdown, because the buttons do not fit.
 *
 * The Configure/Delete pair four CRUD tables share is `editDeleteActions()` beside this —
 * reach for that before spelling the two out again. The column the actions sit in is
 * `<TableColumn variant="actions">`.
 */
const props = withDefaults(
  defineProps<{
    actions: TableAction<TCommand>[]
    variant?: 'dropdown' | 'buttons'
    trigger?: 'hover' | 'click' | 'contextmenu'
    /**
     * Fixed width for each button (e.g. '170px'). Gives every button the same width so
     * buttons line up in columns across rows even when rows have a different number of
     * actions. Only applies to the 'buttons' variant.
     */
    buttonWidth?: string
  }>(),
  {
    variant: 'dropdown',
    trigger: 'click',
    buttonWidth: undefined
  }
)

const emit = defineEmits<{
  (e: 'command', command: TCommand): void
}>()

const { isMobile } = useViewport()

const asDropdown = computed(
  () => isMobile.value || props.variant === 'dropdown'
)

/** An action with no `visible` is shown; only an explicit `false` hides it. */
const shown = computed(() =>
  props.actions.filter((action) => action.visible !== false)
)
</script>

<template>
  <!-- A row with nothing it can do draws nothing, not a trigger over an empty menu. -->
  <Dropdown
    v-if="shown.length && asDropdown"
    :trigger="trigger"
    :commands="actions.map((action) => action.command)"
    @command="emit('command', $event)"
  >
    <DropdownActionButton />
    <template #dropdown>
      <DropdownMenu>
        <DropdownItem
          v-for="action in shown"
          :key="action.command"
          :command="action.command"
          :icon="action.icon"
          :disabled="action.disabled"
          :divided="action.divided"
          :class="{ 'table-actions__danger': action.danger }"
        >
          {{ action.label }}
        </DropdownItem>
      </DropdownMenu>
    </template>
  </Dropdown>

  <div v-else-if="shown.length" class="table-actions__buttons">
    <Button
      v-for="action in shown"
      :key="action.command"
      size="small"
      round
      :disabled="action.disabled"
      :type="action.danger ? 'danger' : undefined"
      :style="buttonWidth ? { width: buttonWidth } : undefined"
      @click="emit('command', action.command)"
    >
      <Icon v-if="action.icon">
        <component :is="action.icon" />
      </Icon>
      <span>{{ action.label }}</span>
    </Button>
  </div>
</template>

<style lang="scss" scoped>
.table-actions__buttons {
  display: flex;
}

:deep(.table-actions__danger) {
  color: var(--el-color-danger);
}
</style>
